import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import {
  DropdownMenu,
  DropdownMenuTrigger,
  DropdownMenuContent,
  DropdownMenuCheckboxItem,
  DropdownMenuSub,
  DropdownMenuSubTrigger,
  DropdownMenuSubContent,
} from "@/components/ui/dropdown-menu";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Tooltip, TooltipTrigger, TooltipContent } from "@/components/ui/tooltip";
import { Search, X, ChevronLeft, ChevronRight, ChevronDown, Check, Crown, Handshake, Languages, Loader2, Link } from "lucide-react";


import CommunityLibraryCard from "@/components/CommunityLibraryCard";
import { CommunityLiveWatchCard } from "@/components/community/CommunityLiveWatchCard";
import { CpmLeaderboardTicker } from "@/components/cpm/CpmLeaderboardTicker";

import CopyLibraryDialog from "@/components/CopyLibraryDialog";
import type { PreloadedCampaignLibrary } from "@/components/CampaignVideoCard";
import UploadModal from "@/components/UploadModal";
import { useEffect, useMemo, useState, useCallback, useRef } from "react";
import { useTranslation } from "react-i18next";
import { useCommunityLibrariesWithCampaignsQuery, useCommunityChannelsQuery, useCommunityLiveSessionsQuery, useMyDataQuery, useActiveCpmCampaignChannelsQuery, useChangeOnboardingObjectiveMutation, MyLibrary, CommunityLiveSessionsQuery } from "@/generated/graphql";
import { useWindowScroll } from "@uidotdev/usehooks";
import { useSearchParams, useNavigate, useLocation } from "react-router-dom";
import { useUrlUpload } from "@/hooks/use-url-upload";
import { isPaidSubscription } from "@/lib/quota/isPaidSubscription";
import { isYoutubeVideoUrl } from "@/lib/video-links/isYoutubeVideoUrl";
import { libraryHasActiveCpmCampaign } from "@/lib/quota/libraryHasActiveCpmCampaign";
import { useIsPaidSubscriber } from "@/lib/quota/useIsPaidSubscriber";
import { useHasFeature } from "@/lib/useHasFeature";
import { useQuotaUpsellDialog } from "@/hooks/use-quota-upsell-dialog";
import { resolveCommunityChannelDeepLink } from "@/lib/resolveCommunityChannelDeepLink";
import { communityMatchToPreloadedLibrary } from "@/lib/library/buildCommunityLibraryDeepLink";
import { resolveAcervoCreatedDestination } from "@/lib/library/resolveAcervoCreatedDestination";
import { isOwnBrandAcervoTeaser } from "@/lib/library/isOwnBrandAcervoTeaser";
import { liveSessionToCopyLibrary } from "@/lib/live-watch/liveSessionToCopyLibrary";
import { AcervoTeaserOverlay } from '@/components/AcervoTeaserOverlay';
import { ObjectiveSwitchConfirmDialog } from '@/components/ObjectiveSwitchConfirmDialog';
import { resolveClipperMonthlyClipLimit } from '@/lib/onboarding/resolveClipperMonthlyClipLimit';
import { ClipperFeatureNagModal } from '@/components/ClipperFeatureNagModal';
import { useClipperFeatureNag } from '@/hooks/use-clipper-feature-nag';
import { useToast } from '@/hooks/use-toast';
import { matchLiveSessionForChannel } from '@/lib/live-watch/matchLiveSessionForChannel';
import { CommunityCampaignCarousel } from '@/components/campaigns/CommunityCampaignCarousel';
import { useDragScroll } from '@/hooks/use-drag-scroll';
import { buildCommunityCampaignCards } from '@/lib/campaigns/communityCampaignCards';
import {
  ALL_COMMUNITY_LANGUAGES,
  COMMUNITY_LANGUAGE_FILTERS,
  parseCommunityLanguageFilter,
  type CommunityLanguageFilter,
} from '@/lib/community/communityLanguageFilter';

const PAGE_SIZE = 12;

const COMMUNITY_LANGUAGE_LABEL_KEYS: Record<CommunityLanguageFilter, string> = {
  'pt-BR': 'communityLibrary.languagePtBr',
  'en-US': 'communityLibrary.languageEnUs',
};

// Locale-independent K/M suffix (Intl.NumberFormat's compact notation spells
// this out as "mil"/"mi" in pt-BR, too long for the narrow channel-strip card).
function formatCompactCount(count: number): string {
  if (count >= 1_000_000) return `${(count / 1_000_000).toFixed(1)}M`;
  if (count >= 1_000) return `${(count / 1_000).toFixed(1)}K`;
  return String(count);
}

const ChannelLiveBadge = ({ label }: { label: string }) => (
  <span className="absolute right-1 top-1 inline-flex items-center gap-1 rounded-full bg-red-500 px-1.5 py-0.5 text-[8px] font-bold uppercase leading-none text-white ring-2 ring-background">
    <span className="h-1.5 w-1.5 animate-pulse rounded-full bg-white" aria-hidden="true" />
    {label}
  </span>
);

type ParsedSort =
  | { kind: 'newest' | 'oldest' | 'views' | 'subscribers' }
  | { kind: 'remixes'; window: '24h' | '7d' | '30d' | '90d' | 'all' };

const REMIX_WINDOW_TOKENS: Array<'24h' | '7d' | '30d' | '90d'> = ['24h', '7d', '30d', '90d'];

function parseSort(sort: string): ParsedSort {
  if (sort === 'remixes') return { kind: 'remixes', window: 'all' };
  if (sort.startsWith('remixes_')) {
    const token = sort.slice('remixes_'.length) as '24h' | '7d' | '30d' | '90d';
    if (REMIX_WINDOW_TOKENS.includes(token)) {
      return { kind: 'remixes', window: token };
    }
    // Unknown remix token → treat as all-time for UI purposes.
    return { kind: 'remixes', window: 'all' };
  }
  if (sort === 'newest' || sort === 'oldest' || sort === 'views' || sort === 'subscribers') {
    return { kind: sort };
  }
  return { kind: 'newest' };
}

function formatSortLabel(t: ReturnType<typeof useTranslation>['t'], parsed: ParsedSort): string {
  switch (parsed.kind) {
    case 'newest': return t('communityLibrary.sortNewest');
    case 'oldest': return t('communityLibrary.sortOldest');
    case 'views': return t('communityLibrary.sortViews');
    case 'subscribers': return t('communityLibrary.sortSubscribers');
    case 'remixes': {
      const windowKey =
        parsed.window === '24h' ? 'sortRemixesWindow24h' :
        parsed.window === '7d'  ? 'sortRemixesWindow7d'  :
        parsed.window === '30d' ? 'sortRemixesWindow30d' :
        parsed.window === '90d' ? 'sortRemixesWindow90d' :
                                  'sortRemixesWindowAll';
      return t('communityLibrary.sortRemixesLabel', {
        window: t(`communityLibrary.${windowKey}`),
      });
    }
  }
}

const CommunityLibraryPage = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  // Widened from Pro+-only to any paid plan on 03/08/2026 (CEO request) — see
  // deriveCampaignAccess.ts docstring for the backend caveat.
  const isPaidUser = useIsPaidSubscriber();
  const hasProFeatures = useHasFeature('pro_features');
  const { dialog: quotaUpsellDialog, openQuotaUpsell } = useQuotaUpsellDialog();
  const [searchParams, setSearchParams] = useSearchParams();
  const initialSearch = searchParams.get('search') || '';
  // Captured once on mount; consumed by the deep-link effect.
  const initialChannelIdRef = useRef(searchParams.get('channelId'));
  const initialLibraryIdRef = useRef(searchParams.get('libraryId'));
  const channelDeepLinkApplied = useRef(false);
  const libraryHighlightApplied = useRef(false);
  const libraryHighlightAttempts = useRef(0);
  const [hasMore, setHasMore] = useState(true);
  const [isFetchingMore, setIsFetchingMore] = useState(false);
  const isFetchingMoreRef = useRef(false);
  const [scroll] = useWindowScroll();
  const scrollY = scroll?.y ?? 0;
  // Seeded from `location.state.preloadedLibrary` (CampaignVideoCard → community page) so we
  // can open the copy dialog without waiting for the community query to resolve.
  const preloadedLibrary = (location.state as { preloadedLibrary?: PreloadedCampaignLibrary } | null)?.preloadedLibrary ?? null;
  const preloadedOpenedRef = useRef(false);
  const [copyDialogLibrary, setCopyDialogLibrary] = useState<MyLibrary | PreloadedCampaignLibrary | null>(null);
  const [searchQuery, setSearchQuery] = useState(initialSearch);
  const [debouncedSearch, setDebouncedSearch] = useState(initialSearch);
  const [selectedChannelId, setSelectedChannelId] = useState<string | null>(null);
  // Campaign cards in the strip filter the grid by CAMPAIGN, not by channel: the
  // channel is attached for the showcase card, and filtering by it would pull in
  // the channel's whole catalog (plus, via collectCampaignLibraryExtras, the
  // campaign's cross-channel videos). Mutually exclusive with selectedChannelId.
  const [selectedCampaignId, setSelectedCampaignId] = useState<string | null>(null);
  const [selectedChannelType, setSelectedChannelType] = useState<string | null>(null);
  const [selectedLanguage, setSelectedLanguage] = useState<CommunityLanguageFilter | null>(null);

  const { toast } = useToast();
  const { data: myData, loading: myDataLoading, refetch: refetchMyData } = useMyDataQuery();
  const isTeaser = isOwnBrandAcervoTeaser(myData?.myData?.onboarding_objective);
  const [teaserSwitchOpen, setTeaserSwitchOpen] = useState(false);
  const [changeOnboardingObjective, { loading: teaserSwitchLoading }] = useChangeOnboardingObjectiveMutation();
  const { open: isClipperFeatureNagOpen, onContinue: handleClipperFeatureNagContinue } =
    useClipperFeatureNag('community-library');
  const isOtherChannels = myData?.myData?.onboarding_objective === 'other_channels';
  const isPaidPlan = useMemo(
    () => isPaidSubscription(myData?.myData?.subscription),
    [myData?.myData?.subscription],
  );
  const cpmUnlocked = useMemo(
    () => libraryHasActiveCpmCampaign(copyDialogLibrary),
    [copyDialogLibrary],
  );
  const canSubmitLibraryVideos = isPaidPlan || isOtherChannels;

  const {
    setVideoUrl,
    urlVideoMetadata,
    isLoadingMetadata,
    communityMatch, setCommunityMatch,
    resetUpload,
    handleUrlUpload,
  } = useUrlUpload({
    maxVideoSeconds: myData?.myData?.subscription?.plan?.max_library_video_seconds,
    hasProAccess: hasProFeatures,
    onProRequired: () => openQuotaUpsell('paid_feature'),
  });
  const [sortBy, setSortBy] = useState<string>("remixes_7d");
  // Passed into every card so the per-card "(N this week)" parenthetical only
  // renders when a *windowed* remix sort is active (not for 'remixes' all-time).
  const activeRemixWindow = useMemo(() => {
    const parsed = parseSort(sortBy);
    return parsed.kind === 'remixes' && parsed.window !== 'all' ? parsed.window : null;
  }, [sortBy]);
  const [channelSortBy, setChannelSortBy] = useState<string>("subscribers");
  const [smartInputValue, setSmartInputValue] = useState(initialSearch);
  const [smartInputHighlighted, setSmartInputHighlighted] = useState(false);
  const isUrlMode = isYoutubeVideoUrl(smartInputValue);
  const handleSmartInputChange = useCallback((value: string) => {
    setSmartInputValue(value);
    if (!isYoutubeVideoUrl(value)) {
      setSearchQuery(value);
    }
  }, []);
  // Drag-to-scroll for the channel strip; handleChannelClick ignores the
  // click that ends a drag (shared with the campaign row).
  const {
    ref: channelScrollRef,
    dragHandlers: channelDragHandlers,
    guardClick: handleChannelClick,
  } = useDragScroll<HTMLDivElement>();

  const handleCurateLive = useCallback((session: CommunityLiveSessionsQuery['communityLiveSessions'][number]) => {
    const source = liveSessionToCopyLibrary(session);
    if (!source?.source_library_id) return;
    setCopyDialogLibrary({
      id: source.source_library_id,
      name: source.name,
      base_video_url: source.base_video_url,
      duration_seconds: source.duration_seconds,
      format: source.format,
      language: source.language,
      start_time: source.start_time,
      end_time: source.end_time,
    } as MyLibrary);
  }, []);

  // Debounce search input
  useEffect(() => {
    const timer = setTimeout(() => setDebouncedSearch(searchQuery), 300);
    return () => clearTimeout(timer);
  }, [searchQuery]);

  useEffect(() => {
    if (myDataLoading) return;
    if (isTeaser) {
      setCopyDialogLibrary(null);
      return;
    }
    if (preloadedLibrary && !preloadedOpenedRef.current) {
      preloadedOpenedRef.current = true;
      setCopyDialogLibrary(preloadedLibrary);
    }
  }, [isTeaser, myDataLoading, preloadedLibrary]);

  const handleTeaserSwitchToClipping = async () => {
    try {
      await changeOnboardingObjective({ variables: { to: 'other_channels' } });
      await refetchMyData();
      setTeaserSwitchOpen(false);
    } catch (error) {
      const isInvalidObjectiveChange = error instanceof Error
        && error.message.includes('invalid_objective_change');
      if (isInvalidObjectiveChange) {
        const refetched = await refetchMyData();
        if (refetched.data?.myData?.onboarding_objective === 'other_channels') {
          setTeaserSwitchOpen(false);
          return;
        }
      }
      toast({
        title: t('account.switchToClippingChannels.error'),
        variant: 'destructive',
      });
    }
  };

  // Fetch libraries with server-side search and channel filtering
  const queryVariables = useMemo(() => ({
    limit: PAGE_SIZE,
    offset: 0,
    sort: sortBy,
    ...(debouncedSearch.trim() ? { search: debouncedSearch.trim() } : {}),
    ...(selectedChannelId ? { channelId: selectedChannelId } : {}),
    ...(selectedCampaignId ? { campaignId: selectedCampaignId } : {}),
    ...(selectedChannelType ? { channelCategory: selectedChannelType } : {}),
    ...(selectedLanguage ? { language: selectedLanguage } : {}),
  }), [debouncedSearch, selectedChannelId, selectedCampaignId, selectedChannelType, selectedLanguage, sortBy]);

  const { data, loading, error, fetchMore, refetch } = useCommunityLibrariesWithCampaignsQuery({
    variables: queryVariables,
    fetchPolicy: 'network-only',
  });

  const libraries = data?.communityLibraries ?? [];
  // Reset pagination when filters change
  useEffect(() => {
    setHasMore(true);
  }, [debouncedSearch, selectedChannelId, selectedCampaignId, selectedChannelType, selectedLanguage, sortBy]);

  // Community libraries are already filtered server-side (originals only, no remixes)
  const communityLibraries = libraries;

  const hasActiveFilters = !!(debouncedSearch || selectedChannelId || selectedCampaignId || selectedChannelType || selectedLanguage);

  // Fetch unfiltered results to show when filtered results are empty
  // Uses no-cache to avoid overwriting the main query's cache entry (keyArgs: false)
  const { data: unfilteredData, loading: unfilteredLoading } = useCommunityLibrariesWithCampaignsQuery({
    variables: { limit: PAGE_SIZE, offset: 0, sort: sortBy },
    fetchPolicy: 'no-cache',
    skip: loading || communityLibraries.length > 0 || !hasActiveFilters,
  });
  const unfilteredLibraries = unfilteredData?.communityLibraries ?? [];

  // Poll for processing libraries
  const hasProcessingLibraries = useMemo(() => {
    return libraries.some((lib) => !['success', 'failure', 'expired'].includes(lib.curation_status));
  }, [libraries]);

  useEffect(() => {
    if (!hasProcessingLibraries) return;
    const pollInterval = setInterval(() => {
      refetch();
    }, 10000);
    return () => clearInterval(pollInterval);
  }, [hasProcessingLibraries, refetch]);

  // Fetch channels via dedicated query (server-side filtering)
  const { data: channelsData, loading: channelsLoading } = useCommunityChannelsQuery({
    variables: {
      ...(selectedChannelType ? { category: selectedChannelType } : {}),
      sort: channelSortBy,
    },
  });
  const rawChannels = useMemo(
    () => channelsData?.communityChannels ?? [],
    [channelsData?.communityChannels],
  );
  const { data: liveSessionsData } = useCommunityLiveSessionsQuery({
    pollInterval: 30000,
  });
  const liveSessions = useMemo(
    () => liveSessionsData?.communityLiveSessions ?? [],
    [liveSessionsData?.communityLiveSessions],
  );
  const visibleLiveSessions = useMemo(() => {
    const visibleChannelIds = new Set(rawChannels.map((channel) => channel.id));
    return liveSessions.filter((session) => (
      visibleChannelIds.has(session.youtube_channel.id)
      && (!selectedChannelId || session.youtube_channel.id === selectedChannelId)
    ));
  }, [liveSessions, rawChannels, selectedChannelId]);

  // Fetch all channels (no category filter) to derive unique categories
  const { data: allChannelsData, loading: allChannelsLoading } = useCommunityChannelsQuery();
  const allChannels = useMemo(
    () => allChannelsData?.communityChannels ?? [],
    [allChannelsData?.communityChannels],
  );

  // Active CPM campaigns, one card each, for the "Campanhas pagando agora"
  // row above the channel strip (CommunityCampaignCarousel). Built from the
  // campaigns themselves, so a campaign with no YouTube channel still shows.
  const { data: campaignChannelsData } = useActiveCpmCampaignChannelsQuery({ fetchPolicy: 'no-cache', pollInterval: 60_000, context: { queryDeduplication: false } });
  const campaignCards = useMemo(
    () => buildCommunityCampaignCards(campaignChannelsData?.myCpmCampaigns ?? [], { isPaidUser }),
    [campaignChannelsData, isPaidUser],
  );

  const carouselChannels = useMemo(
    () => [...rawChannels].sort((a, b) => {
      if (a.is_autoclipper_partner && !b.is_autoclipper_partner) return -1;
      if (!a.is_autoclipper_partner && b.is_autoclipper_partner) return 1;
      return 0;
    }),
    [rawChannels],
  );

  const channelCategories = useMemo(() => {
    const categorySet = new Set<string>();
    allChannels.forEach((ch) => {
      if (ch.category) categorySet.add(ch.category);
    });
    return Array.from(categorySet).sort((a, b) => a.localeCompare(b));
  }, [allChannels]);


  const scrollChannels = (direction: 'left' | 'right') => {
    if (!channelScrollRef.current) return;
    const scrollAmount = 280;
    channelScrollRef.current.scrollBy({
      left: direction === 'left' ? -scrollAmount : scrollAmount,
      behavior: 'smooth',
    });
  };

  const handleLoadMore = useCallback(async () => {
    if (isFetchingMoreRef.current || loading || error || !hasMore) return;
    isFetchingMoreRef.current = true;
    setIsFetchingMore(true);
    try {
      const prevCount = libraries.length;
      const res = await fetchMore({
        variables: { ...queryVariables, offset: prevCount },
      });
      const fetched = res.data?.communityLibraries?.length ?? 0;
      if (fetched < PAGE_SIZE) setHasMore(false);
    } finally {
      isFetchingMoreRef.current = false;
      setIsFetchingMore(false);
    }
  }, [loading, error, hasMore, libraries.length, fetchMore, queryVariables]);

  useEffect(() => {
    if (loading || !hasMore) return;
    const currentY = scrollY ?? 0;
    const viewportH = window.innerHeight || document.documentElement.clientHeight;
    const docH = document.documentElement.scrollHeight || document.body.scrollHeight;
    const distanceFromBottom = docH - (currentY + viewportH);
    if (distanceFromBottom < 600) {
      handleLoadMore();
    }
  }, [scrollY, hasMore, loading, handleLoadMore]);

  useEffect(() => {
    if (!loading && libraries.length > 0 && libraries.length < PAGE_SIZE) {
      setHasMore(false);
    }
  }, [loading, libraries.length]);

  // Deep link from /campaigns/:id — apply ?channelId once we know the channel set.
  useEffect(() => {
    if (channelDeepLinkApplied.current) return;
    const target = initialChannelIdRef.current;
    if (!target) {
      channelDeepLinkApplied.current = true;
      return;
    }
    if (channelsLoading || allChannelsLoading) return;
    const match = resolveCommunityChannelDeepLink(target, rawChannels, allChannels);
    if (match) {
      setSelectedChannelId(match.id);
      setSelectedCampaignId(null);
    }
    channelDeepLinkApplied.current = true;
  }, [rawChannels, allChannels, channelsLoading, allChannelsLoading]);

  // Deep link from /campaigns/:id — open the create-clip modal for the targeted
  // library, plus a scroll & ring-highlight on its card for context after the
  // modal closes. Paginates forward up to 5 times if the library isn't yet on
  // the current page.
  useEffect(() => {
    if (libraryHighlightApplied.current) return;
    const target = initialLibraryIdRef.current;
    if (!target) {
      libraryHighlightApplied.current = true;
      return;
    }
    if (myDataLoading) return;
    // If modal was already opened via preloaded state, skip the data-wait.
    if (preloadedLibrary?.id === target) {
      libraryHighlightApplied.current = true;
      if (searchParams.has('channelId') || searchParams.has('libraryId')) {
        setSearchParams((prev) => { prev.delete('channelId'); prev.delete('libraryId'); return prev; }, { replace: true });
      }
      return;
    }
    // Wait until the channel deep link has been resolved so the right query is in flight.
    if (!channelDeepLinkApplied.current) return;
    if (loading) return;

    const match = libraries.find((lib) => lib.id === target);
    if (match) {
      if (!isTeaser) setCopyDialogLibrary(match as MyLibrary);

      const node = document.querySelector(
        `[data-library-id="${CSS.escape(target)}"]`,
      ) as HTMLElement | null;
      if (node) {
        node.scrollIntoView({ behavior: 'smooth', block: 'center' });
        const ringClasses = ['ring-2', 'ring-primary', 'ring-offset-2', 'ring-offset-background'];
        node.classList.add(...ringClasses);
        setTimeout(() => node.classList.remove(...ringClasses), 2500);
      }

      libraryHighlightApplied.current = true;
      setSearchParams((prev) => {
        prev.delete('channelId');
        prev.delete('libraryId');
        return prev;
      }, { replace: true });
      return;
    }

    if (libraryHighlightAttempts.current < 5 && hasMore) {
      libraryHighlightAttempts.current += 1;
      handleLoadMore();
      return;
    }

    libraryHighlightApplied.current = true;
    setSearchParams((prev) => {
      prev.delete('channelId');
      prev.delete('libraryId');
      return prev;
    }, { replace: true });
  }, [libraries, loading, hasMore, handleLoadMore, setSearchParams, isTeaser, myDataLoading]);

  // Deep link from the Sidebar's "Criar novo projeto" button — scroll & ring-highlight
  // the smart search/upload input so the user sees exactly where to act. Watches
  // `searchParams` directly (not a mount-only ref) so it also fires when the user
  // is already on this page and clicks the button again — `navigate()` still
  // produces a new searchParams instance even without a remount. Driven by React
  // state rather than direct classList mutation, so it survives re-renders cleanly.
  useEffect(() => {
    if (searchParams.get('focus') !== 'video-url-input') return;

    const input = document.getElementById('video-url-input');
    if (input) {
      input.focus();
      input.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }
    setSmartInputHighlighted(true);

    setSearchParams((prev) => {
      prev.delete('focus');
      return prev;
    }, { replace: true });
  }, [searchParams, setSearchParams]);

  useEffect(() => {
    if (!smartInputHighlighted) return;
    const timer = setTimeout(() => setSmartInputHighlighted(false), 2500);
    return () => clearTimeout(timer);
  }, [smartInputHighlighted]);

  return (
    <div className="min-h-screen bg-background p-4 lg:p-6 relative">
      {/* Radial green glow — top only, fades before the video grid */}
      <div
        className="absolute top-0 left-0 right-0 h-72 pointer-events-none"
        style={{ background: 'radial-gradient(ellipse 70% 288px at 50% 0%, hsl(var(--primary) / 0.07) 0%, transparent 100%)' }}
      />
      <div className="relative w-full max-w-screen-2xl mx-auto space-y-5">

        {/* Smart Search — unified search + YouTube URL upload */}
        <div id="community-search-sort" className="flex flex-col items-center space-y-3">
          <div className="text-center space-y-1">
            <h1 className="text-2xl lg:text-3xl font-bold text-primary">
              {t('communityLibrary.pageTitle')}
            </h1>
            <p className="text-sm text-muted-foreground">
              {t('communityLibrary.searchSubtitle')}
            </p>
          </div>
          <div id="community-upload-card" className="flex flex-col sm:flex-row w-full max-w-3xl gap-2">
            {/* Smart input: detects YouTube URL vs search text */}
            <div className="relative flex-1">
              {/* Icon-positioning context scoped to just the input row, so the
                  examples line below never shifts these via top-1/2. */}
              <div className="relative">
                <div
                  data-testid="community-smart-input-decoration"
                  aria-hidden="true"
                  className="pointer-events-none absolute left-4 top-1/2 -translate-y-1/2 flex items-center gap-1"
                >
                  <Search className="w-4 h-4 text-muted-foreground" />
                  <Link className="w-4 h-4 text-muted-foreground" />
                </div>
                <Input
                  id="video-url-input"
                  placeholder={t('communityLibrary.smartSearchPlaceholder')}
                  value={smartInputValue}
                  onChange={(e) => handleSmartInputChange(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key !== 'Enter') return;
                    if (isUrlMode) {
                      if (canSubmitLibraryVideos) handleUrlUpload(smartInputValue);
                      return;
                    }
                    // Mobile keyboards have no visible "results updated" cue, so the
                    // user has no way to tell the search worked without scrolling
                    // manually. Pressing the keyboard's "Go"/"OK" fires this same
                    // Enter keydown — blur to dismiss the keyboard, then jump to
                    // the grid so the results are immediately visible.
                    e.currentTarget.blur();
                    document.getElementById('community-video-grid')
                      ?.scrollIntoView({ behavior: 'smooth', block: 'start' });
                  }}
                  className={`pl-16 pr-10 h-11 text-sm transition-all border-primary/40 shadow-[0_0_12px_hsl(var(--primary)/0.15)] focus-visible:border-primary/70 focus-visible:shadow-[0_0_18px_hsl(var(--primary)/0.25)] ${isUrlMode ? 'border-primary/70' : ''} ${smartInputHighlighted ? 'ring-2 ring-primary ring-offset-2 ring-offset-background animate-pulse' : ''}`}
                />
                {smartInputValue && (
                  <button
                    onClick={() => { setSmartInputValue(''); setSearchQuery(''); }}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
                  >
                    <X className="w-4 h-4" />
                  </button>
                )}
              </div>

              {/* Static examples for the two smart-input actions — each stays
                  intact as a unit (whitespace-nowrap) so narrow/mobile widths
                  wrap the whole example to the next line instead of splitting
                  it mid-phrase; aligned under the field itself. */}
              <div className="mt-1.5 flex flex-wrap items-center justify-center gap-x-2 gap-y-0.5 text-xs text-muted-foreground">
                <span className="whitespace-nowrap">{t('communityLibrary.searchExample')}</span>
                <span className="text-muted-foreground/40">•</span>
                <span className="whitespace-nowrap">{t('communityLibrary.urlExample')}</span>
              </div>
            </div>

            {/* Analyze button — URL mode only */}
            {isUrlMode && (
              canSubmitLibraryVideos ? (
                <Button
                  variant="gradient"
                  className="h-11 px-4 shrink-0"
                  onClick={() => handleUrlUpload(smartInputValue)}
                  disabled={isLoadingMetadata}
                >
                  {isLoadingMetadata ? (
                    <Loader2 className="w-4 h-4 animate-spin" />
                  ) : (
                    t('library.createClips')
                  )}
                </Button>
              ) : (
                <Button
                  variant="outline"
                  className="h-11 px-4 shrink-0 gap-1.5"
                  onClick={() => openQuotaUpsell('paid_feature')}
                >
                  <Crown className="w-4 h-4" />
                  {t('library.createClips')}
                </Button>
              )
            )}

            {/* Sort dropdown — search mode only */}
            {!isUrlMode && (() => {
              const parsed = parseSort(sortBy);
              const isRemix = parsed.kind === 'remixes';
              const activeWindow = isRemix ? parsed.window : null;
              return (
                <DropdownMenu modal={false}>
                  <DropdownMenuTrigger asChild>
                    <button
                      type="button"
                      aria-label={t('communityLibrary.sortBy')}
                      className="flex items-center justify-between w-full sm:w-[220px] h-11 px-3 py-2 text-sm border border-input bg-background rounded-md hover:bg-accent/50 focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2 focus:ring-offset-background"
                    >
                      <span className="truncate text-left">{formatSortLabel(t, parsed)}</span>
                      <ChevronDown className="w-4 h-4 ml-2 opacity-50 shrink-0" />
                    </button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="end" className="w-[220px] sm:w-[220px] min-w-[var(--radix-dropdown-menu-trigger-width)]">
                    <DropdownMenuCheckboxItem checked={parsed.kind === 'newest'} onCheckedChange={() => setSortBy('newest')}>
                      {t('communityLibrary.sortNewest')}
                    </DropdownMenuCheckboxItem>
                    <DropdownMenuCheckboxItem checked={parsed.kind === 'oldest'} onCheckedChange={() => setSortBy('oldest')}>
                      {t('communityLibrary.sortOldest')}
                    </DropdownMenuCheckboxItem>
                    <DropdownMenuCheckboxItem checked={parsed.kind === 'views'} onCheckedChange={() => setSortBy('views')}>
                      {t('communityLibrary.sortViews')}
                    </DropdownMenuCheckboxItem>
                    <DropdownMenuCheckboxItem checked={parsed.kind === 'subscribers'} onCheckedChange={() => setSortBy('subscribers')}>
                      {t('communityLibrary.sortSubscribers')}
                    </DropdownMenuCheckboxItem>
                    <DropdownMenuSub>
                      <DropdownMenuSubTrigger className="focus:text-accent-foreground data-[state=open]:text-accent-foreground">
                        <Check className={`w-4 h-4 mr-2 ${isRemix ? 'opacity-100' : 'opacity-0'}`} />
                        {t('communityLibrary.sortRemixes')}
                      </DropdownMenuSubTrigger>
                      <DropdownMenuSubContent className="w-[180px]">
                        <DropdownMenuCheckboxItem checked={activeWindow === '24h'} onCheckedChange={() => setSortBy('remixes_24h')}>
                          {t('communityLibrary.sortRemixesWindow24h')}
                        </DropdownMenuCheckboxItem>
                        <DropdownMenuCheckboxItem checked={activeWindow === '7d'} onCheckedChange={() => setSortBy('remixes_7d')}>
                          {t('communityLibrary.sortRemixesWindow7d')}
                        </DropdownMenuCheckboxItem>
                        <DropdownMenuCheckboxItem checked={activeWindow === '30d'} onCheckedChange={() => setSortBy('remixes_30d')}>
                          {t('communityLibrary.sortRemixesWindow30d')}
                        </DropdownMenuCheckboxItem>
                        <DropdownMenuCheckboxItem checked={activeWindow === '90d'} onCheckedChange={() => setSortBy('remixes_90d')}>
                          {t('communityLibrary.sortRemixesWindow90d')}
                        </DropdownMenuCheckboxItem>
                        <DropdownMenuCheckboxItem checked={activeWindow === 'all'} onCheckedChange={() => setSortBy('remixes')}>
                          {t('communityLibrary.sortRemixesWindowAll')}
                        </DropdownMenuCheckboxItem>
                      </DropdownMenuSubContent>
                    </DropdownMenuSub>
                  </DropdownMenuContent>
                </DropdownMenu>
              );
            })()}

            {/* Language filter — search mode only, like the sort */}
            {!isUrlMode && (
              <Select
                value={selectedLanguage ?? ALL_COMMUNITY_LANGUAGES}
                onValueChange={(value) => setSelectedLanguage(parseCommunityLanguageFilter(value))}
              >
                <SelectTrigger
                  aria-label={t('communityLibrary.languageFilter')}
                  data-testid="community-language-filter"
                  className={`w-full sm:w-[150px] h-11 shrink-0 gap-2 ${selectedLanguage ? 'border-primary/60' : ''}`}
                >
                  <Languages className="w-4 h-4 shrink-0 text-muted-foreground" aria-hidden="true" />
                  {/* Unfiltered, the trigger names the filter ("Idioma") rather than
                      echoing the long "Todos os idiomas" item, so it fits the row. */}
                  <span className="flex-1 min-w-0 truncate text-left">
                    <SelectValue>
                      {selectedLanguage
                        ? t(COMMUNITY_LANGUAGE_LABEL_KEYS[selectedLanguage])
                        : t('communityLibrary.languageFilterShort')}
                    </SelectValue>
                  </span>
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value={ALL_COMMUNITY_LANGUAGES}>{t('communityLibrary.languageAll')}</SelectItem>
                  {COMMUNITY_LANGUAGE_FILTERS.map((language) => (
                    <SelectItem key={language} value={language}>
                      {t(COMMUNITY_LANGUAGE_LABEL_KEYS[language])}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            )}
          </div>

        </div>

        <CommunityCampaignCarousel
          cards={campaignCards}
          selectedCampaignId={selectedCampaignId}
          onToggleSelect={(campaignId) => {
            setSelectedChannelId(null);
            setSelectedCampaignId((current) => (current === campaignId ? null : campaignId));
          }}
          onOpen={(card) => navigate(card.isLocked ? '/plans' : `/campaigns/${card.id}`)}
        />

        {/* Channel carousel skeleton — keeps the section from flashing empty
            on first load (channels arrive from a separate query). */}
        {channelsLoading && rawChannels.length === 0 && (
          <div className="space-y-2" aria-hidden="true">
            {/* Category chip placeholders */}
            <div className="flex items-center gap-2">
              <div className="flex gap-2 overflow-hidden flex-1 min-w-0">
                {Array.from({ length: 6 }).map((_, idx) => (
                  <div
                    key={`cat-skel-${idx}`}
                    className="h-6 w-24 rounded-full bg-muted animate-pulse flex-shrink-0"
                  />
                ))}
              </div>
              <div className="h-8 w-[150px] rounded-md bg-muted animate-pulse flex-shrink-0" />
            </div>
            {/* Channel avatar placeholders */}
            <div className="flex items-center gap-3 overflow-hidden px-4 py-1">
              {Array.from({ length: 12 }).map((_, idx) => (
                <div key={`chan-skel-${idx}`} className="flex flex-col items-center gap-1.5 flex-shrink-0 w-20 p-2">
                  <div className="w-12 h-12 rounded-full bg-muted animate-pulse" />
                  <div className="h-2.5 w-14 rounded bg-muted animate-pulse" />
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Channel carousel */}
        {rawChannels.length > 0 && (
          <div id="community-channels" className="space-y-2">
            {/* Category chips + sort — single scrollable row */}
            <div className="flex items-center gap-2">
              <div className="flex gap-2 overflow-x-auto scrollbar-hide flex-1 min-w-0" style={{ scrollbarWidth: 'none' }}>
                <button
                  onClick={() => { setSelectedChannelType(null); setSelectedChannelId(null); setSelectedCampaignId(null); }}
                  className={`px-3 py-1 rounded-full text-xs font-medium transition-colors whitespace-nowrap flex-shrink-0 ${
                    selectedChannelType === null
                      ? 'bg-primary text-primary-foreground'
                      : 'bg-muted text-muted-foreground hover:text-foreground'
                  }`}
                >
                  {t('communityLibrary.allTypes')}
                </button>
                {channelCategories.map((category) => (
                  <button
                    key={category}
                    onClick={() => {
                      setSelectedChannelType(selectedChannelType === category ? null : category);
                      setSelectedChannelId(null);
                      setSelectedCampaignId(null);
                    }}
                    className={`px-3 py-1 rounded-full text-xs font-medium transition-colors whitespace-nowrap flex-shrink-0 ${
                      selectedChannelType === category
                        ? 'bg-primary text-primary-foreground'
                        : 'bg-muted text-muted-foreground hover:text-foreground'
                    }`}
                  >
                    {t(`communityLibrary.categories.${category}`, category)}
                  </button>
                ))}
              </div>
              <Select value={channelSortBy} onValueChange={setChannelSortBy}>
                <SelectTrigger className="w-[150px] h-8 text-xs flex-shrink-0">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="name">{t('communityLibrary.channelSortName')}</SelectItem>
                  <SelectItem value="remixes">{t('communityLibrary.channelSortRemixes')}</SelectItem>
                  <SelectItem value="subscribers">{t('communityLibrary.channelSortSubscribers')}</SelectItem>
                </SelectContent>
              </Select>
            </div>

            {/* Channel carousel */}
            <div className="relative group/carousel">
              {/* Left fade gradient */}
              <div className="absolute left-0 top-0 bottom-0 w-12 bg-gradient-to-r from-background to-transparent z-[5] pointer-events-none" />
              {/* Right fade gradient */}
              <div className="absolute right-0 top-0 bottom-0 w-12 bg-gradient-to-l from-background to-transparent z-[5] pointer-events-none" />

              {/* Left scroll button */}
              <button
                onClick={() => scrollChannels('left')}
                className="absolute left-2 top-1/2 -translate-y-1/2 z-10 w-8 h-8 rounded-full bg-background/90 border border-border shadow-sm flex items-center justify-center opacity-0 group-hover/carousel:opacity-100 transition-opacity hover:bg-muted"
              >
                <ChevronLeft className="w-4 h-4" />
              </button>

              <div
                ref={channelScrollRef}
                className="flex items-center gap-3 overflow-x-auto scrollbar-hide px-4 py-1"
                {...channelDragHandlers}
                style={{ ...channelDragHandlers.style, scrollbarWidth: 'none', msOverflowStyle: 'none' }}
              >
                {/* "All" option */}
                <button
                  onClick={() => handleChannelClick(() => {
                    setSelectedChannelId(null);
                    setSelectedCampaignId(null);
                  })}
                  className={`flex flex-col items-center gap-1.5 flex-shrink-0 p-2 rounded-xl transition-all w-20 border-2 ${
                    selectedChannelId === null && selectedCampaignId === null
                      ? 'bg-gradient-to-b from-primary/20 to-primary/5 border-primary ring-2 ring-primary/30'
                      : 'border-border/50 hover:bg-muted hover:border-border'
                  }`}
                >
                  <div className={`w-12 h-12 rounded-full flex items-center justify-center text-[10px] font-bold shadow-sm ${
                    selectedChannelId === null && selectedCampaignId === null
                      ? 'bg-gradient-to-br from-primary to-primary/80 text-primary-foreground'
                      : 'bg-muted text-muted-foreground'
                  }`}>
                    {t('communityLibrary.allChannels')}
                  </div>
                  <span className={`text-[10px] font-semibold truncate w-full text-center leading-tight ${
                    selectedChannelId === null && selectedCampaignId === null ? 'text-primary' : ''
                  }`}>
                    {t('communityLibrary.allChannels')}
                  </span>
                </button>

                {carouselChannels.map((channel) => {
                  const isSelected = selectedChannelId === channel.id;
                  const liveSession = matchLiveSessionForChannel(liveSessions, channel.id);

                  return (
                    <button
                      key={channel.id}
                      onClick={() => handleChannelClick(() => {
                        setSelectedChannelId(isSelected ? null : channel.id);
                        setSelectedCampaignId(null);
                      })}
                      className={`relative flex flex-col items-center justify-center gap-1.5 flex-shrink-0 self-stretch p-2 rounded-xl transition-all w-20 border-2 ${
                        isSelected
                          ? 'border-transparent bg-primary/10 ring-2 ring-primary'
                          : channel.is_autoclipper_partner
                            ? 'border-dashed border-primary/50 hover:border-primary hover:bg-primary/5'
                            : 'border-transparent hover:bg-muted'
                      }`}
                    >
                      {liveSession && (
                        <ChannelLiveBadge label={t('communityLibrary.liveWatch.badge')} />
                      )}
                      <div className="relative">
                        {channel.thumbnail ? (
                          <img
                            src={channel.thumbnail}
                            alt={channel.name}
                            className={`w-12 h-12 rounded-full object-cover border-2 transition-colors pointer-events-none ${isSelected ? 'border-primary' : 'border-transparent'}`}
                            draggable={false}
                          />
                        ) : (
                          <div className={`w-12 h-12 rounded-full bg-muted flex items-center justify-center text-sm font-bold border-2 transition-colors ${isSelected ? 'border-primary' : 'border-transparent'}`}>
                            {channel.name.charAt(0).toUpperCase()}
                          </div>
                        )}
                        {channel.is_autoclipper_partner && (
                          <Tooltip>
                            <TooltipTrigger asChild>
                              <div className="absolute -top-0.5 -right-0.5 w-4 h-4 rounded-full bg-primary flex items-center justify-center ring-2 ring-background cursor-default">
                                <Handshake className="w-2.5 h-2.5 text-primary-foreground" />
                              </div>
                            </TooltipTrigger>
                            <TooltipContent side="top">
                              <p>Canal Parceiro</p>
                            </TooltipContent>
                          </Tooltip>
                        )}
                      </div>
                      <span className="text-[10px] font-medium line-clamp-2 w-full text-center leading-tight">
                        {channel.name.split(' | ')[0]}
                      </span>
                      {channel.is_autoclipper_partner && channel.subscriber_count != null && (
                        <span className="text-[9px] text-muted-foreground line-clamp-2 w-full text-center leading-tight">
                          {formatCompactCount(channel.subscriber_count)} {t('communityLibrary.subscribers')}
                        </span>
                      )}
                    </button>
                  );
                })}
              </div>

              {/* Right scroll button */}
              <button
                onClick={() => scrollChannels('right')}
                className="absolute right-2 top-1/2 -translate-y-1/2 z-10 w-8 h-8 rounded-full bg-background/90 border border-border shadow-sm flex items-center justify-center opacity-0 group-hover/carousel:opacity-100 transition-opacity hover:bg-muted"
              >
                <ChevronRight className="w-4 h-4" />
              </button>
            </div>

          </div>
        )}

        {/* Clipper ranking — a slim scrolling strip where the old earn banner
            sat, so it never pushes the campaigns and videos below the fold. */}
        <CpmLeaderboardTicker ranksLink={isOtherChannels} />

        {isTeaser && (
          <AcervoTeaserOverlay
            onSwitch={() => setTeaserSwitchOpen(true)}
          />
        )}

        {/* Video Grid */}
        <div id="community-video-grid" className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4 w-full">
            {visibleLiveSessions.map((session) => (
              <div
                key={`live-${session.id}`}
                className={`rounded-xl transition-shadow duration-500${isTeaser ? ' pointer-events-none select-none blur-md' : ''}`}
              >
                <CommunityLiveWatchCard
                  session={session}
                  onCurate={isTeaser ? undefined : handleCurateLive}
                />
              </div>
            ))}
            {loading && Array.from({ length: 6 }).map((_, idx) => (
              <Card key={`skeleton-${idx}`} className="p-0 animate-pulse overflow-hidden">
                <div className="aspect-video bg-muted" />
                <div className="p-3 space-y-2">
                  <div className="h-4 bg-muted rounded w-3/4" />
                  <div className="h-3 bg-muted rounded w-1/2" />
                  <div className="h-8 bg-muted rounded w-full" />
                </div>
              </Card>
            ))}

            {!loading && !error && communityLibraries.map((library) => (
              <div
                key={library.id}
                data-library-id={library.id}
                className={`rounded-xl transition-shadow duration-500${isTeaser ? ' pointer-events-none select-none blur-md' : ''}`}
              >
                <CommunityLibraryCard
                  library={library as MyLibrary}
                  onGenerateClips={isTeaser ? () => undefined : (lib) => setCopyDialogLibrary(lib)}
                  activeRemixWindow={activeRemixWindow}
                />
              </div>
            ))}

            {!loading && !error && communityLibraries.length === 0 && visibleLiveSessions.length === 0 && (
              <div className="col-span-full text-center py-8">
                <p className="text-sm text-muted-foreground">{t('library.noItemsFound')}</p>
              </div>
            )}
            {error && (
              <div className="col-span-full text-center text-sm text-destructive">
                {t('library.failedToLoad')}
              </div>
            )}
            {isFetchingMore && (
              <div className="col-span-full text-center text-xs text-muted-foreground py-2">{t('library.loadingMore')}</div>
            )}
        </div>

        {/* Unfiltered results when filters return empty */}
        {!loading && !error && communityLibraries.length === 0 && hasActiveFilters && (
          <div className="space-y-4">
            <h3 className="text-sm font-semibold text-muted-foreground">
              {t('communityLibrary.seeMoreVideos')}
            </h3>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
              {unfilteredLoading && Array.from({ length: 6 }).map((_, idx) => (
                <Card key={`skeleton-unfiltered-${idx}`} className="p-0 animate-pulse overflow-hidden">
                  <div className="aspect-video bg-muted" />
                  <div className="p-3 space-y-2">
                    <div className="h-4 bg-muted rounded w-3/4" />
                    <div className="h-3 bg-muted rounded w-1/2" />
                    <div className="h-8 bg-muted rounded w-full" />
                  </div>
                </Card>
              ))}
              {!unfilteredLoading && unfilteredLibraries.map((library) => (
                <div
                  key={`unfiltered-${library.id}`}
                  className={isTeaser ? 'pointer-events-none select-none blur-md' : undefined}
                >
                <CommunityLibraryCard
                  library={library as MyLibrary}
                  onGenerateClips={isTeaser ? () => undefined : (lib) => setCopyDialogLibrary(lib)}
                  activeRemixWindow={activeRemixWindow}
                />
                </div>
              ))}
            </div>
          </div>
        )}
      </div>

      <CopyLibraryDialog
        isOpen={!!copyDialogLibrary}
        onClose={() => setCopyDialogLibrary(null)}
        title={t('library.generateAIClips')}
        isPaidPlan={isPaidPlan}
        cpmUnlocked={cpmUnlocked}
        library={copyDialogLibrary ? {
          name: copyDialogLibrary.name,
          base_video_url: copyDialogLibrary.base_video_url,
          duration_seconds: copyDialogLibrary.duration_seconds,
          format: copyDialogLibrary.format,
          language: copyDialogLibrary.language,
          audio_language: 'audio_language' in copyDialogLibrary ? copyDialogLibrary.audio_language : null,
          start_time: copyDialogLibrary.start_time,
          end_time: copyDialogLibrary.end_time,
          source_library_id: copyDialogLibrary.id,
        } : null}
      />

      {/* Upload Modal */}
      <UploadModal
        isOpen={!!urlVideoMetadata}
        onClose={() => { resetUpload(); setSmartInputValue(''); }}
        onUploadComplete={(created) => {
          resetUpload();
          setSmartInputValue('');
          navigate(resolveAcervoCreatedDestination({
            objective: myData?.myData?.onboarding_objective,
            createdId: created?.id,
          }));
        }}
        videoMetadata={urlVideoMetadata}
        isUploadComplete={true}
        isUploaded={false}
        mode={myData?.myData?.onboarding_objective === 'own_brand' ? 'project' : 'library'}
      />

      {/* Community Library Available Dialog */}
      <Dialog open={!!communityMatch} onOpenChange={(open) => {
        if (!open) {
          setCommunityMatch(null);
        }
      }}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{t('library.communityAvailableTitle')}</DialogTitle>
            <DialogDescription>
              {t('library.communityAvailableDescription')}
            </DialogDescription>
          </DialogHeader>
          <DialogFooter className="sm:justify-center">
            <Button
              variant="gradient"
              onClick={() => {
                if (!communityMatch) return;
                if (!isTeaser) {
                  setCopyDialogLibrary(communityMatchToPreloadedLibrary(communityMatch));
                }
                setCommunityMatch(null);
                setSmartInputValue('');
                setVideoUrl("");
              }}
            >
              <Search className="w-4 h-4 mr-2" />
              {t('library.goToCommunityLibrary')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      {quotaUpsellDialog}
      <ObjectiveSwitchConfirmDialog
        to={teaserSwitchOpen ? 'other_channels' : null}
        clipperMonthlyClipLimit={resolveClipperMonthlyClipLimit(
          myData?.myData?.subscription?.plan?.max_library_exports,
        )}
        loading={teaserSwitchLoading}
        onCancel={() => setTeaserSwitchOpen(false)}
        onConfirm={() => {
          void handleTeaserSwitchToClipping();
        }}
      />
      <ClipperFeatureNagModal
        open={isClipperFeatureNagOpen}
        destination="community-library"
        onContinue={handleClipperFeatureNagContinue}
      />
    </div>
  );
};

export default CommunityLibraryPage;
